// src/components/MsoVersionTimeline.tsx
//
// ★★ ANNEX A 22 — one rule's whole version history, and the comparison of any two versions.
//
// Oldest first, because that is the order the rule changed in. Every version states its status in
// WORDS (a status that is only a colour is not a status), its effective window, who activated it
// and when, and the provenance note it carried. Selecting two versions opens the side-by-side.
import { useState } from 'react'
import { Link } from 'react-router'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { formatInstant, formatIsoDate } from '../lib/format'
import { ErrorCard, LoadingCard } from './StateCards'
import { CategoryBadge } from './StatusBadge'
import { VersionDiff } from './MsoDefinitionView'
import type { MsoRuleStatus, MsoRuleVersion } from '../lib/types'

const STATUS_WORDS: Readonly<Record<MsoRuleStatus, string>> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  SUPERSEDED: 'Superseded',
  RETIRED: 'Retired',
}

export function MsoStatusBadge({ status }: { status: MsoRuleStatus }) {
  return <CategoryBadge kind="ruleStatus" value={status} label={STATUS_WORDS[status] ?? status} />
}

const label = (v: MsoRuleVersion): string => `${v.ruleKey} v${v.version}`

/**
 * A person as a name. 'Not recorded' only when there is neither a name nor an id to have named, and a SEEDED version (one
 * no officer created: the Authority pack's three rules ship with the system) says so instead, because "Created by Not
 * recorded" on an in-force rule reads as a defect to someone seeing it for the first time.
 */
export const personText = (name: string | null | undefined, id: string | null, seeded = false): string =>
  name ?? (id === null ? (seeded ? 'Supplied with the system' : 'Not recorded') : 'Name not available')

/** True for a version nobody created or edited through the screens: it came with the system. */
export const isSeededVersion = (v: { createdBy: string | null; lastEditedBy: string | null }): boolean =>
  v.createdBy === null && v.lastEditedBy === null

export function MsoVersionTimeline({ ruleKey }: { ruleKey: string }) {
  const history = useApi(() => api.msoRuleVersions(ruleKey), [ruleKey])
  const [picked, setPicked] = useState<readonly string[]>([])

  if (history.error !== null) {
    return <ErrorCard title={`Could not load the versions of ${ruleKey}`} message={history.error} {...(history.retryable && { onRetry: history.reload })} />
  }
  if (history.initialLoading || history.data === undefined) return <LoadingCard label="Loading versions" rows={2} />

  const versions = history.data.versions
  const byId = new Map(versions.map((v) => [v.id, v]))
  const [leftId, rightId] = picked
  const left = leftId === undefined ? undefined : byId.get(leftId)
  const right = rightId === undefined ? undefined : byId.get(rightId)

  /** At most two selected: choosing a third drops the earliest choice, so the comparison never goes stale. */
  const toggle = (id: string) =>
    setPicked((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id].slice(-2)))

  return (
    <div className="flex flex-col gap-4" data-testid={`versions-${ruleKey}`}>
      <div className="pt-table-wrap">
        <table className="pt-table">
          <caption className="sr-only">Every version of {ruleKey}, oldest first</caption>
          <thead>
            <tr>
              <th scope="col" className="pt-no-print" data-unsortable="a version history is read oldest first as the rule changed; reordering it would break the timeline">
                Compare
              </th>
              <th scope="col" data-unsortable="a version history is read oldest first as the rule changed; reordering it would break the timeline">
                Version
              </th>
              <th scope="col" data-unsortable="a version history is read oldest first as the rule changed; reordering it would break the timeline">
                Status
              </th>
              <th scope="col" data-unsortable="a version history is read oldest first as the rule changed; reordering it would break the timeline">
                Effective
              </th>
              <th scope="col" data-unsortable="a version history is read oldest first as the rule changed; reordering it would break the timeline">
                Created by
              </th>
              <th scope="col" data-unsortable="a version history is read oldest first as the rule changed; reordering it would break the timeline">
                Last edited by
              </th>
              <th scope="col" data-unsortable="a version history is read oldest first as the rule changed; reordering it would break the timeline">
                Activated
              </th>
              <th scope="col" data-unsortable="a version history is read oldest first as the rule changed; reordering it would break the timeline">
                Provenance note
              </th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.id}>
                <td className="pt-no-print">
                  <input
                    type="checkbox"
                    checked={picked.includes(v.id)}
                    onChange={() => toggle(v.id)}
                    aria-label={`Compare ${label(v)}`}
                  />
                </td>
                <td className="pt-mono font-semibold whitespace-nowrap">
                  <Link to={`/administration/mso-rules/versions/${v.id}`}>v{v.version}</Link>
                </td>
                <td>
                  <MsoStatusBadge status={v.status} />
                </td>
                <td className="text-sm">
                  {formatIsoDate(v.effectiveFrom)}
                  {v.effectiveUntil === null ? ' onward' : ` to ${formatIsoDate(v.effectiveUntil)}`}
                </td>
                <td className="text-sm">{personText(v.createdByName, v.createdBy, isSeededVersion(v))}</td>
                <td className="text-sm">{personText(v.lastEditedByName, v.lastEditedBy, isSeededVersion(v))}</td>
                <td className="text-sm">
                  {v.activatedAt === null ? (
                    'Not activated'
                  ) : (
                    <>
                      {formatInstant(v.activatedAt)}
                      <br />
                      <span className="text-xs">{personText(v.activatedByName, v.activatedBy, isSeededVersion(v))}</span>
                    </>
                  )}
                </td>
                <td className="text-sm max-w-[40ch]">{v.provenanceNote ?? 'None recorded'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-sm text-[var(--sgds-body-color-subtle)]" role="status">
        {left !== undefined && right !== undefined
          ? `Comparing ${label(left)} with ${label(right)}.`
          : 'Tick two versions to compare their decision tables side by side.'}
      </p>
      {left !== undefined && right !== undefined && (
        <VersionDiff leftLabel={label(left)} rightLabel={label(right)} left={left.definition} right={right.definition} />
      )}
    </div>
  )
}
